import { useAuth } from '@/components/providers';
import ReferralService from '@/services/ReferralService';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { REFERRAL } from '@/utils/strings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
interface ReferralContextType {
  isLoading: boolean;
  error: Error | null;
  userReferralCode: string | null;
  hasUsedReferralCode: boolean;
  usedReferralCode: string | null;
  generateReferralCode: () => void;
  applyReferralCode: (code: string) => Promise<boolean>;
  getApplyErrorMessage: (errorType?: string) => {
    title: string;
    description: string;
  };
  refreshReferralData: () => void;
}
const ReferralContext = createContext<ReferralContextType | undefined>(
  undefined,
);
export const useReferral = () => {
  const context = useContext(ReferralContext);
  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
};
const PROVIDER_NAME = 'ReferralProvider';
export const ReferralProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const QUERY_KEY_REFERRAL = ['referral'];
  useEffect(() => {
    if (user) {
      logDebug('ReferralProvider mounted with user', PROVIDER_NAME, {
        userId: user.uid,
      });
    }
  }, [user]);
  const {
    data: referralData,
    isLoading,
    error,
    refetch: refreshReferralData,
  } = useQuery({
    queryKey: QUERY_KEY_REFERRAL,
    queryFn: async () => {
      try {
        logInfo('Fetching referral data', `${PROVIDER_NAME}.queryFn`);
        return await ReferralService.getReferralData();
      } catch (error) {
        logError(error, `${PROVIDER_NAME}.queryFn`);
        return null;
      }
    },
    staleTime: 24 * 60 * 60 * 1000,
    enabled: !!user,
    placeholderData: (previousData) => previousData,
  });
  const createReferralCodeMutation = useMutation({
    mutationFn: async () => {
      logInfo(
        'Creating referral code',
        `${PROVIDER_NAME}.createReferralCodeMutation.mutationFn`,
      );
      try {
        const result = await ReferralService.createReferralCode();
        logInfo(
          'ReferralService.createReferralCode result',
          `${PROVIDER_NAME}.createReferralCodeMutation.mutationFn`,
          { result },
        );
        return result;
      } catch (error) {
        logError(
          error,
          `${PROVIDER_NAME}.createReferralCodeMutation.mutationFn`,
        );
        throw error;
      }
    },
    onSuccess: (data) => {
      logInfo(
        'Referral code created successfully',
        `${PROVIDER_NAME}.createReferralCodeMutation.onSuccess`,
        { code: data },
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_REFERRAL });
    },
    onError: (error) => {
      logError(error, `${PROVIDER_NAME}.createReferralCodeMutation.onError`);
    },
  });
  const applyReferralCodeMutation = useMutation<
    { success: boolean; errorType?: string; authorizationChanged?: boolean },
    Error,
    string
  >({
    mutationFn: async (code: string) => {
      logInfo(
        'Applying referral code',
        `${PROVIDER_NAME}.applyReferralCodeMutation.mutationFn`,
        {
          code,
        },
      );
      try {
        const result = await ReferralService.applyReferralCode(code, true);
        logInfo(
          'ReferralService.applyReferralCode result',
          `${PROVIDER_NAME}.applyReferralCodeMutation.mutationFn`,
          {
            result,
            code,
          },
        );
        return result;
      } catch (error) {
        logError(
          error,
          `${PROVIDER_NAME}.applyReferralCodeMutation.mutationFn`,
          { code },
        );
        throw error;
      }
    },
    onSuccess: (result, variables) => {
      logInfo(
        'Referral code applied successfully',
        `${PROVIDER_NAME}.applyReferralCodeMutation.onSuccess`,
      );
      const currentData = queryClient.getQueryData(QUERY_KEY_REFERRAL);
      if (currentData && result.success) {
        queryClient.setQueryData(QUERY_KEY_REFERRAL, {
          ...currentData,
          hasUsedReferralCode: true,
          referredBy: variables,
        });
      }
    },
    onError: (error) => {
      logError(error, `${PROVIDER_NAME}.applyReferralCodeMutation.onError`);
    },
  });
  const userReferralCode = referralData?.referralCode || null;
  const hasUsedReferralCode = referralData?.hasUsedReferralCode || false;
  const usedReferralCode = referralData?.referredBy || null;
  const isProviderLoading =
    isLoading ||
    createReferralCodeMutation.isPending ||
    applyReferralCodeMutation.isPending;
  const generateReferralCode = useCallback(() => {
    logInfo(
      'Generating referral code',
      `${PROVIDER_NAME}.generateReferralCode`,
    );
    createReferralCodeMutation.mutate();
  }, [createReferralCodeMutation]);
  const value = useMemo(
    () => ({
      isLoading: isProviderLoading,
      error,
      userReferralCode,
      hasUsedReferralCode,
      usedReferralCode,
      generateReferralCode,
      applyReferralCode: async (code: string) => {
        logInfo(
          'applyReferralCode called',
          `${PROVIDER_NAME}.applyReferralCode`,
          {
            code,
          },
        );
        try {
          logDebug(
            'About to call ReferralService.applyReferralCode directly',
            `${PROVIDER_NAME}.applyReferralCode`,
            { code },
          );
          const result = await ReferralService.applyReferralCode(code, true);
          logInfo(
            'ReferralService.applyReferralCode completed',
            `${PROVIDER_NAME}.applyReferralCode`,
            { result, code },
          );
          if (result.success) {
            const currentData = queryClient.getQueryData(QUERY_KEY_REFERRAL);
            if (currentData) {
              queryClient.setQueryData(QUERY_KEY_REFERRAL, {
                ...currentData,
                hasUsedReferralCode: true,
                referredBy: code,
              });
            }
          }
          return result.success;
        } catch (error) {
          logError(error, `${PROVIDER_NAME}.applyReferralCode`, { code });
          return false;
        }
      },
      getApplyErrorMessage: (errorType?: string) => {
        switch (errorType) {
          case 'network':
            return {
              title: REFERRAL.APPLY_NETWORK_ERROR_TITLE,
              description: REFERRAL.APPLY_NETWORK_ERROR_DESCRIPTION,
            };
          case 'server':
            return {
              title: REFERRAL.APPLY_SERVER_ERROR_TITLE,
              description: REFERRAL.APPLY_SERVER_ERROR_DESCRIPTION,
            };
          case 'already_used':
            return {
              title: REFERRAL.APPLY_ALREADY_USED_ERROR_TITLE,
              description: REFERRAL.APPLY_ALREADY_USED_ERROR_DESCRIPTION,
            };
          case 'auth':
          case 'invalid':
          case 'api':
          case 'unknown':
          default:
            return {
              title: REFERRAL.APPLY_ERROR_TITLE,
              description: REFERRAL.APPLY_ERROR_DESCRIPTION,
            };
        }
      },
      refreshReferralData: () => {
        logDebug(
          'Manual refresh of referral data requested',
          `${PROVIDER_NAME}.refreshReferralData`,
        );
        refreshReferralData();
      },
    }),
    [
      isProviderLoading,
      error,
      userReferralCode,
      hasUsedReferralCode,
      usedReferralCode,
      generateReferralCode,
      applyReferralCodeMutation,
      refreshReferralData,
    ],
  );
  logDebug('ReferralProvider render', PROVIDER_NAME, {
    isLoading: value.isLoading,
    userReferralCode,
    hasUsedReferralCode,
  });
  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
};
export default ReferralProvider;
